/**
 * Cascading FK Picker for magic bridge resolution.
 *
 * This picker filters its options based on prior selections (parent pickers).
 * Used when a view has magic bridges where an intermediate table has no visible
 * fields but its sub-joins do.
 *
 * Example: Employment (bridge) with Person and LegalEntity (sub-joins)
 * - Selecting Person filters LegalEntity to only those with matching Employment
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  FormLabel,
  Tooltip,
} from "@mui/joy";
import { useCascadedReferenceOptions } from "../../hooks/useMagicBridge";
import type {
  ExtendedJoinedTableInfo,
  CascadingSelection,
} from "../../types/magicBridge";

/**
 * Format a technical alias into a human-readable label.
 */
function formatAliasAsLabel(alias: string): string {
  return alias
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

interface ReferenceOption {
  id: string;
  displayValue: string;
  secondaryInfo?: string;
  fieldValues?: Record<string, string | null>;
}

interface CascadingFkPickerProps {
  viewId: string;
  joinInfo: ExtendedJoinedTableInfo;
  value: CascadingSelection | null;
  onChange: (selection: CascadingSelection | null) => void;
  /** Current selections from other pickers (alias -> selectedId) */
  parentSelections: Record<string, string>;
  /** Whether this picker is disabled (waiting for parent selection) */
  disabled?: boolean;
  /** Message explaining why picker is disabled */
  disabledReason?: string;
  /** Callback when FK column name is resolved from API */
  onFkColumnNameResolved?: (alias: string, fkColumnName: string) => void;
  /** Optional size variant */
  size?: "sm" | "md";
}

/**
 * Cascading FK Picker component.
 *
 * Features:
 * - Filters options based on parent selections via cascaded-reference-options API
 * - Shows disabled state with explanation when parents not selected
 * - Shows dependency badges indicating what this picker depends on
 * - Supports nullable fields with "(None)" option
 */
export default function CascadingFkPicker({
  viewId,
  joinInfo,
  value,
  onChange,
  parentSelections,
  disabled = false,
  disabledReason,
  onFkColumnNameResolved,
  size = "md",
}: CascadingFkPickerProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Check if all required parent selections are made
  const allParentsSelected = useMemo(() => {
    return joinInfo.dependsOnAliases.every(
      (alias) => parentSelections[alias] && parentSelections[alias].length > 0,
    );
  }, [joinInfo.dependsOnAliases, parentSelections]);

  // Only enable query if not disabled and all parents selected
  const queryEnabled = !disabled && allParentsSelected && !!joinInfo.joinId;

  // Use cascaded reference options hook
  const { data, isLoading, isFetching } = useCascadedReferenceOptions(
    viewId,
    joinInfo.joinId,
    parentSelections,
    debouncedSearch,
    queryEnabled,
  );

  // When we get the fkColumnName from the response, pass it up
  useEffect(() => {
    if (data?.fkColumnName && onFkColumnNameResolved) {
      onFkColumnNameResolved(joinInfo.alias, data.fkColumnName);
    }
  }, [data?.fkColumnName, joinInfo.alias, onFkColumnNameResolved]);

  // Convert API response to options
  const options: ReferenceOption[] = useMemo(() => {
    const apiOptions = data?.options || [];
    return apiOptions.map((opt) => ({
      id: opt.id,
      displayValue: opt.displayValue,
      secondaryInfo: opt.secondaryInfo,
      fieldValues: opt.fieldValues,
    }));
  }, [data?.options]);

  // Add "None" option for nullable fields
  const allOptions: ReferenceOption[] = useMemo(() => {
    if (joinInfo.isRequired) {
      return options;
    }
    return [{ id: "", displayValue: "(None)" }, ...options];
  }, [options, joinInfo.isRequired]);

  // Handle selection change
  const handleChange = useCallback(
    (_: unknown, newValue: ReferenceOption | null) => {
      if (!newValue || newValue.id === "") {
        onChange(null);
      } else {
        onChange({
          alias: joinInfo.alias,
          selectedId: newValue.id,
          selectedDisplayValue: newValue.displayValue,
          fieldValues: newValue.fieldValues,
        });
      }
    },
    [onChange, joinInfo.alias],
  );

  // Convert value to ReferenceOption format
  const selectedOption: ReferenceOption | null = useMemo(() => {
    if (!value) return null;
    return {
      id: value.selectedId,
      displayValue: value.selectedDisplayValue,
    };
  }, [value]);

  // Determine display state
  const isActuallyDisabled = disabled || !allParentsSelected;
  const showLoading = isLoading || isFetching;

  // Build disabled reason message
  const getDisabledMessage = (): string | undefined => {
    if (disabledReason) return disabledReason;
    if (!allParentsSelected && joinInfo.dependsOnAliases.length > 0) {
      const missingParents = joinInfo.dependsOnAliases
        .filter((alias) => !parentSelections[alias])
        .map(formatAliasAsLabel);
      return `Select ${missingParents.join(", ")} first`;
    }
    return undefined;
  };

  const disabledMessage = getDisabledMessage();

  return (
    <FormControl sx={{ mb: 2 }}>
      <FormLabel required={joinInfo.isRequired}>
        {joinInfo.displayLabel}
      </FormLabel>

      {/* Show dependency badges if this picker depends on others */}
      {joinInfo.dependsOnAliases.length > 0 && (
        <Box sx={{ mb: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {joinInfo.dependsOnAliases.map((depAlias) => {
            const isSelected = !!parentSelections[depAlias];
            return (
              <Chip
                key={depAlias}
                size="sm"
                color={isSelected ? "success" : "neutral"}
                variant={isSelected ? "solid" : "outlined"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              >
                {formatAliasAsLabel(depAlias)}
              </Chip>
            );
          })}
        </Box>
      )}

      <Tooltip title={disabledMessage || ""} placement="top" arrow>
        <span>
          <Autocomplete
            value={selectedOption}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            options={allOptions}
            getOptionLabel={(option) => option.displayValue}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            loading={showLoading}
            disabled={isActuallyDisabled}
            size={size}
            placeholder={
              isActuallyDisabled
                ? disabledMessage || "Waiting for selection..."
                : `Select ${joinInfo.displayLabel}...`
            }
            slotProps={{
              input: {
                endDecorator: showLoading ? (
                  <CircularProgress size="sm" />
                ) : null,
              },
              listbox: {
                sx: { maxHeight: 300 },
              },
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id || "__none__"}>
                <Box>
                  <Typography level="body-sm">{option.displayValue}</Typography>
                  {option.secondaryInfo && (
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      {option.secondaryInfo}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
          />
        </span>
      </Tooltip>
    </FormControl>
  );
}

/**
 * Helper component for displaying bridge disambiguation options.
 * Shows when multiple bridge records match the sub-join selections.
 */
export function BridgeDisambiguationPicker({
  options,
  displayColumns,
  value,
  onChange,
  label,
  size = "md",
}: {
  options: Array<{
    id: string;
    displayValue: string;
    additionalFields: Record<string, string>;
  }>;
  displayColumns: string[];
  value: string | null;
  onChange: (id: string | null) => void;
  label: string;
  size?: "sm" | "md";
}) {
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((opt) => opt.id === value) || null;
  }, [value, options]);

  return (
    <FormControl sx={{ mb: 2 }}>
      <FormLabel required>{label}</FormLabel>
      <Typography level="body-sm" color="warning" sx={{ mb: 1 }}>
        Multiple matches found. Please select the correct record:
      </Typography>
      <Autocomplete
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange(newValue?.id || null);
        }}
        options={options}
        getOptionLabel={(option) => option.displayValue}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        size={size}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography level="body-sm">{option.displayValue}</Typography>
              {displayColumns.length > 0 && (
                <Box
                  sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}
                >
                  {displayColumns.map((col) => (
                    <Chip
                      key={col}
                      size="sm"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.65rem" }}
                    >
                      {col}: {option.additionalFields[col] || "N/A"}
                    </Chip>
                  ))}
                </Box>
              )}
            </Box>
          </li>
        )}
      />
    </FormControl>
  );
}
